import { useEffect, useRef, useCallback, useState } from 'react';
import { AuthAPI } from '../services/api';

interface UseActivityTrackerOptions {
  /** Inactivity timeout in minutes before showing warning (default: from env or 115 minutes) */
  warningTimeoutMinutes?: number;
  /** Time to show warning before auto-logout in minutes (default: 5 minutes) */
  warningDurationMinutes?: number;
  /** Heartbeat interval in minutes (default: 5 minutes) */
  heartbeatIntervalMinutes?: number;
  /** Callback when user is logged out due to inactivity */
  onInactiveLogout: () => void;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
}

interface UseActivityTrackerReturn {
  /** Whether the warning modal should be shown */
  showWarning: boolean;
  /** Seconds remaining before auto-logout */
  secondsRemaining: number;
  /** Call this to reset the inactivity timer (user clicked "Stay logged in") */
  resetTimer: () => void;
  /** Call this to manually trigger logout */
  triggerLogout: () => void;
}

// Events that count as user activity on the page
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
];

// Default timeout from environment or 120 minutes
const DEFAULT_INACTIVITY_TIMEOUT = parseInt(process.env.REACT_APP_SESSION_INACTIVITY_MINUTES || '120', 10);
// Warning duration: 5 minutes or 1/3 of timeout (whichever is smaller), minimum 1 minute
const DEFAULT_WARNING_DURATION = Math.max(1, Math.min(5, Math.floor(DEFAULT_INACTIVITY_TIMEOUT / 3)));
// Send heartbeat every 5 minutes or 1/3 of timeout (whichever is smaller), minimum 1 minute
const DEFAULT_HEARTBEAT_INTERVAL = Math.max(1, Math.min(5, Math.floor(DEFAULT_INACTIVITY_TIMEOUT / 3)));

export function useActivityTracker({
  warningTimeoutMinutes = DEFAULT_INACTIVITY_TIMEOUT - DEFAULT_WARNING_DURATION,
  warningDurationMinutes = DEFAULT_WARNING_DURATION,
  heartbeatIntervalMinutes = DEFAULT_HEARTBEAT_INTERVAL,
  onInactiveLogout,
  isAuthenticated
}: UseActivityTrackerOptions): UseActivityTrackerReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(warningDurationMinutes * 60);
  const [countdownActive, setCountdownActive] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef<boolean>(true);
  const triggerLogoutRef = useRef<() => void>(() => {});
  const countdownEndRef = useRef<number>(0);

  // Clear warning and heartbeat timers (not countdown - that's managed by state)
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  // Handle inactive logout
  const triggerLogout = useCallback(() => {
    clearAllTimers();
    setCountdownActive(false);
    setShowWarning(false);
    onInactiveLogout();
  }, [clearAllTimers, onInactiveLogout]);

  // Keep ref updated with latest triggerLogout
  useEffect(() => {
    triggerLogoutRef.current = triggerLogout;
  }, [triggerLogout]);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async (wasActive: boolean = true) => {
    if (!isAuthenticated) return;

    try {
      await AuthAPI.heartbeat(wasActive);
      // Heartbeat successful, session is still active
    } catch (error: any) {
      if (error.message === 'SESSION_INACTIVE_LOGOUT') {
        // Server says session expired due to inactivity
        sessionStorage.setItem('logoutReason', 'inactivity');
        triggerLogout();
      } else if (error.message === 'USER_DEACTIVATED') {
        // User account was deactivated by admin
        sessionStorage.setItem('logoutReason', 'deactivated');
        triggerLogout();
      }
      // Other errors - don't logout, could be network issues
    }
  }, [isAuthenticated, triggerLogout]);

  // Start the countdown
  const startCountdown = useCallback(() => {
    const totalSeconds = warningDurationMinutes * 60;
    countdownEndRef.current = Date.now() + totalSeconds * 1000;
    setSecondsRemaining(totalSeconds);
    setCountdownActive(true);
  }, [warningDurationMinutes]);

  // Show the warning modal
  const showWarningModal = useCallback(() => {
    setShowWarning(true);
    startCountdown();
  }, [startCountdown]);

  // Countdown effect - uses wall-clock time so it works in background tabs
  useEffect(() => {
    if (!countdownActive) return;

    const tick = () => {
      const remaining = Math.round((countdownEndRef.current - Date.now()) / 1000);

      if (remaining <= 0) {
        clearInterval(intervalId);
        setSecondsRemaining(0);
        setCountdownActive(false);
        setTimeout(() => triggerLogoutRef.current(), 0);
        return;
      }
      setSecondsRemaining(remaining);
    };

    const intervalId = setInterval(tick, 1000);

    // Also recalculate immediately when tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [countdownActive]);

  // Start the warning timer
  const startWarningTimer = useCallback(() => {
    // Clear existing timer
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Set timer to show warning after inactivity period
    warningTimerRef.current = setTimeout(() => {
      showWarningModal();
    }, warningTimeoutMinutes * 60 * 1000);
  }, [warningTimeoutMinutes, showWarningModal]);

  // Reset activity timer (called on user activity or "Stay logged in" click)
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
    setShowWarning(false);
    setCountdownActive(false);

    // Restart warning timer
    startWarningTimer();

    // Send heartbeat immediately when user interacts after warning (they're active)
    sendHeartbeat(true);
  }, [startWarningTimer, sendHeartbeat]);

  // Handle user activity events
  const handleActivity = useCallback(() => {
    // Only reset if not showing warning (user must click "Stay logged in")
    if (!showWarning) {
      lastActivityRef.current = Date.now();
      isActiveRef.current = true;
    }
  }, [showWarning]);

  // Throttled activity handler to avoid excessive calls
  const throttledActivityHandler = useCallback(() => {
    // Throttle to once per second
    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      handleActivity();
    }
  }, [handleActivity]);

  // Setup effect
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Start the warning timer
    startWarningTimer();

    // Start heartbeat interval - always send to check if user is still active/not deactivated
    heartbeatTimerRef.current = setInterval(() => {
      // Always send heartbeat to check user status (deactivation check)
      // Pass whether user was actually active so backend only updates last_activity if true
      const wasActive = isActiveRef.current;
      sendHeartbeat(wasActive);
      // Reset active flag - will be set true again on next activity
      isActiveRef.current = false;
    }, heartbeatIntervalMinutes * 60 * 1000);

    // Send initial heartbeat (user just logged in, so they're active)
    sendHeartbeat(true);

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, throttledActivityHandler, { passive: true });
    });

    // Handle visibility change (tab focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible, check if we should still be logged in
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, throttledActivityHandler);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isAuthenticated,
    startWarningTimer,
    sendHeartbeat,
    heartbeatIntervalMinutes,
    throttledActivityHandler,
    handleActivity,
    clearAllTimers
  ]);

  return {
    showWarning,
    secondsRemaining,
    resetTimer,
    triggerLogout
  };
}
